import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Star, ThumbsUp, FileText, ArrowRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import TierBadge from '../components/TierBadge';
import StarRating from '../components/StarRating';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserDashboard() {
  const { user } = useAuth();
  const [votes, setVotes] = useState([]);
  const [votedAwards, setVotedAwards] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [votesRes, nominationsRes, awardsRes] = await Promise.all([
        axios.get(`${API_URL}/api/votes/user`, { withCredentials: true }),
        axios.get(`${API_URL}/api/nominations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/awards`)
      ]);

      setVotes(votesRes.data);
      setNominations(nominationsRes.data);

      // Get details of voted awards
      const votedAwardIds = votesRes.data.map(v => v.award_id);
      const voted = awardsRes.data.filter(a => votedAwardIds.includes(a.id));
      setVotedAwards(voted);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="h-32 skeleton rounded-xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 skeleton rounded-xl" />
            <div className="h-64 skeleton rounded-xl" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="user-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Welcome Header */}
        <div className="bg-[#121212] rounded-2xl border border-white/5 p-8 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
              <span className="text-2xl font-semibold text-[#0A0A0A]">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-medium text-white">
                Welcome, {user?.name}
              </h1>
              <p className="text-white/50">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mt-8">
            <div className="text-center p-4 bg-[#0A0A0A] rounded-xl">
              <p className="text-3xl font-['Playfair_Display'] font-semibold text-[#D4AF37]">
                {votes.length}
              </p>
              <p className="text-white/50 text-sm">Votes Cast</p>
            </div>
            <div className="text-center p-4 bg-[#0A0A0A] rounded-xl">
              <p className="text-3xl font-['Playfair_Display'] font-semibold text-[#D4AF37]">
                {nominations.length}
              </p>
              <p className="text-white/50 text-sm">Nominations</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Voted Awards */}
          <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Playfair_Display'] text-xl font-semibold text-white flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-[#D4AF37]" />
                Your Votes
              </h2>
              <Link to="/awards" className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1">
                Browse Awards <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {votedAwards.length === 0 ? (
              <div className="text-center py-12">
                <ThumbsUp className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <p className="text-white/50 mb-4">You haven't voted for any awards yet</p>
                <Link to="/awards">
                  <Button className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]">
                    Explore Awards
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {votedAwards.slice(0, 5).map((award) => (
                  <Link
                    key={award.id}
                    to={`/awards/${award.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg bg-[#0A0A0A] hover:bg-[#1A1A1A] transition-colors"
                    data-testid={`voted-award-${award.id}`}
                  >
                    <img
                      src={award.image_url || 'https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=100'}
                      alt={award.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{award.name}</p>
                      <p className="text-white/50 text-sm">{award.location}, {award.country}</p>
                    </div>
                    <TierBadge tier={award.tier} size="sm" showLabel={false} />
                  </Link>
                ))}
                {votedAwards.length > 5 && (
                  <p className="text-center text-white/40 text-sm pt-2">
                    +{votedAwards.length - 5} more
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Nominations */}
          <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Playfair_Display'] text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Your Nominations
              </h2>
              <Link to="/nominate" className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1">
                Submit New <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {nominations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <p className="text-white/50 mb-4">You haven't submitted any nominations</p>
                <Link to="/nominate">
                  <Button className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]">
                    Submit Nomination
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {nominations.slice(0, 5).map((nomination) => (
                  <div
                    key={nomination.id}
                    className="p-3 rounded-lg bg-[#0A0A0A]"
                    data-testid={`my-nomination-${nomination.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{nomination.name}</p>
                        <p className="text-white/50 text-sm">{nomination.location}, {nomination.country}</p>
                      </div>
                      <StatusBadge status={nomination.status} />
                    </div>
                  </div>
                ))}
                {nominations.length > 5 && (
                  <Link to="/nominate" className="block text-center text-[#D4AF37] text-sm pt-2 hover:underline">
                    View all nominations
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/awards" className="block">
            <div className="p-6 bg-[#121212] rounded-xl border border-white/5 hover:border-[#D4AF37]/30 transition-all">
              <Star className="w-8 h-8 text-[#D4AF37] mb-3" />
              <h3 className="text-lg font-semibold text-white mb-1">Explore Awards</h3>
              <p className="text-white/50 text-sm">Discover award-winning destinations</p>
            </div>
          </Link>
          <Link to="/nominate" className="block">
            <div className="p-6 bg-[#121212] rounded-xl border border-white/5 hover:border-[#D4AF37]/30 transition-all">
              <FileText className="w-8 h-8 text-[#D4AF37] mb-3" />
              <h3 className="text-lg font-semibold text-white mb-1">Submit Nomination</h3>
              <p className="text-white/50 text-sm">Recommend an exceptional place</p>
            </div>
          </Link>
        </div>
      </div>
    </main>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending: 'bg-yellow-500/10 text-yellow-500',
    approved: 'bg-green-500/10 text-green-500',
    rejected: 'bg-red-500/10 text-red-500'
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs capitalize ${config[status] || config.pending}`}>
      {status}
    </span>
  );
}
